/**
 * Play transition: organic ink blot expands from the click/tap point, then
 * reveals the next screen/run and fades out. Controlled by settingPlayTransition.
 */

export type PlayTransitionOrigin = { x: number; y: number };

export type PlayTransitionOptions = {
    /** Client coordinates of the click/tap that triggered the action. */
    origin?: PlayTransitionOrigin | null;
    /** When false, skip pointer-lock arming (e.g. Home START → hub). Default true. */
    armPointerLock?: boolean;
};

export type PlayTransitionHooks = {
    /** 0 = clear audio, 1 = full low-pass / distant mix. */
    setTransitionAudioDepth: (depth: number) => void;
    playTransitionSfx: () => void;
    /** Must run on the PLAY click stack so pointer lock can engage. */
    armPointerLockFromGesture: () => void;
    /** Called when the veil is gone so movement can begin. */
    onTransitionComplete?: () => void;
};

const EXPAND_MS = 480;
const HOLD_COVERED_MS = 60;
const FADE_OUT_MS = 180;
const BLOB_VERTS = 40;
/** Max radial wobble as a fraction of radius (organic edge). */
const WOBBLE = 0.16;

let hooks: PlayTransitionHooks | null = null;
let overlay: HTMLElement | null = null;
let canvas: HTMLCanvasElement | null = null;
let ctx: CanvasRenderingContext2D | null = null;
let running = false;
let lastPointer: PlayTransitionOrigin = { x: 0, y: 0 };
let pointerTracked = false;

function readBool(key: string, defaultOn: boolean): boolean {
    try {
        const raw = localStorage.getItem(key);
        if (raw === "0") return false;
        if (raw === "1") return true;
    } catch {
        /* ignore */
    }
    return defaultOn;
}

export function isPlayTransitionEnabled(): boolean {
    return readBool("settingPlayTransition", true);
}

export function isModesSelectPanelEnabled(): boolean {
    return readBool("settingModesSelectPanel", true);
}

/** True while the play overlay is on screen (including fade-out). */
export function isPlayTransitionRunning(): boolean {
    return running;
}

function easeOutCubic(t: number): number {
    return 1 - (1 - t) ** 3;
}

function easeInOutCubic(t: number): number {
    return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}

function trackPointer(e: PointerEvent): void {
    lastPointer = { x: e.clientX, y: e.clientY };
}

function ensurePointerTracking(): void {
    if (pointerTracked) return;
    pointerTracked = true;
    window.addEventListener("pointerdown", trackPointer, { capture: true, passive: true });
    window.addEventListener("pointermove", trackPointer, { capture: true, passive: true });
}

function resolveOrigin(origin?: PlayTransitionOrigin | null): PlayTransitionOrigin {
    if (origin && Number.isFinite(origin.x) && Number.isFinite(origin.y)) {
        return { x: origin.x, y: origin.y };
    }
    if (lastPointer.x || lastPointer.y) return { ...lastPointer };
    return { x: window.innerWidth * 0.5, y: window.innerHeight * 0.5 };
}

function inkColor(): string {
    const mid = getComputedStyle(document.documentElement).getPropertyValue("--btn-sky-mid").trim();
    if (mid) return mid;
    const main = getComputedStyle(document.documentElement).getPropertyValue("--btn-main-bg").trim();
    return main || "#66ffff";
}

function inkEdgeColor(): string {
    const edge = getComputedStyle(document.documentElement).getPropertyValue("--btn-sky-edge").trim();
    return edge || "#317a7a";
}

function parseCssHex(raw: string): { r: number; g: number; b: number } | null {
    const s = raw.trim();
    const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(s);
    if (!m) return null;
    let h = m[1]!;
    if (h.length === 3) h = h[0]! + h[0]! + h[1]! + h[1]! + h[2]! + h[2]!;
    return {
        r: parseInt(h.slice(0, 2), 16),
        g: parseInt(h.slice(2, 4), 16),
        b: parseInt(h.slice(4, 6), 16),
    };
}

function relativeLuminance255(r: number, g: number, b: number): number {
    const lin = (c: number) => {
        const x = c / 255;
        return x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** Dark title on light buttons; light title when button fill is too dark to read edge text. */
function inkTitleColor(fill: string, edge: string): string {
    const rgb = parseCssHex(fill);
    if (!rgb) return edge;
    if (relativeLuminance255(rgb.r, rgb.g, rgb.b) > 0.55) return edge;
    const light = getComputedStyle(document.documentElement).getPropertyValue("--btn-sky-light").trim();
    if (light) return light;
    const mix = (c: number) => Math.round(c + (255 - c) * 0.4);
    const toHex = (n: number) => n.toString(16).padStart(2, "0");
    return `#${toHex(mix(rgb.r))}${toHex(mix(rgb.g))}${toHex(mix(rgb.b))}`;
}

function ensureOverlay(): HTMLElement {
    if (overlay) return overlay;

    overlay = document.createElement("div");
    overlay.id = "playTransitionOverlay";
    overlay.className = "playTransitionOverlay hidden";
    overlay.setAttribute("aria-hidden", "true");

    canvas = document.createElement("canvas");
    canvas.className = "playTransitionInk";
    canvas.setAttribute("aria-hidden", "true");
    overlay.appendChild(canvas);
    document.body.appendChild(overlay);

    ctx = canvas.getContext("2d");
    return overlay;
}

function resizeCanvas(): void {
    if (!canvas) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const w = window.innerWidth;
    const h = window.innerHeight;
    canvas.width = Math.max(1, Math.floor(w * dpr));
    canvas.height = Math.max(1, Math.floor(h * dpr));
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
}

type BlobProfile = {
    radii: Float32Array;
    seed: number;
};

function makeBlobProfile(seed: number): BlobProfile {
    const radii = new Float32Array(BLOB_VERTS);
    for (let i = 0; i < BLOB_VERTS; i++) {
        const a = (i / BLOB_VERTS) * Math.PI * 2;
        const wobble =
            Math.sin(a * 3 + seed) * 0.55 +
            Math.sin(a * 5 - seed * 1.3) * 0.28 +
            Math.sin(a * 7 + seed * 0.7) * 0.12 +
            Math.sin(a * 11 + seed * 2.1) * 0.05;
        radii[i] = 1 + wobble * WOBBLE;
    }
    return { radii, seed };
}

function coverRadius(ox: number, oy: number, profile: BlobProfile): number {
    const corners: PlayTransitionOrigin[] = [
        { x: 0, y: 0 },
        { x: window.innerWidth, y: 0 },
        { x: 0, y: window.innerHeight },
        { x: window.innerWidth, y: window.innerHeight },
    ];
    let maxDist = 0;
    for (const c of corners) {
        const d = Math.hypot(c.x - ox, c.y - oy);
        if (d > maxDist) maxDist = d;
    }
    let minFactor = 1;
    for (let i = 0; i < profile.radii.length; i++) {
        if (profile.radii[i]! < minFactor) minFactor = profile.radii[i]!;
    }
    // Extra pad so irregular lobes still paint past every corner.
    return (maxDist / Math.max(0.72, minFactor)) * 1.12;
}

function blobPath(ox: number, oy: number, radius: number, profile: BlobProfile): void {
    if (!ctx) return;
    const { radii } = profile;
    ctx.beginPath();
    for (let i = 0; i <= BLOB_VERTS; i++) {
        const idx = i % BLOB_VERTS;
        const a = (idx / BLOB_VERTS) * Math.PI * 2;
        const r = radius * radii[idx]!;
        const x = ox + Math.cos(a) * r;
        const y = oy + Math.sin(a) * r;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
    }
    ctx.closePath();
}

function drawTitle(titleColor: string): void {
    if (!ctx) return;
    const cx = window.innerWidth * 0.5;
    const cy = window.innerHeight * 0.5;
    const fontPx = Math.max(14, Math.min(42, Math.floor(window.innerWidth * 0.045)));
    ctx.font = `${fontPx}px "Press Start 2P", monospace`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = titleColor;
    ctx.fillText("CURSORGAME", cx, cy);
}

function drawInkBlob(
    ox: number,
    oy: number,
    radius: number,
    profile: BlobProfile,
    fill: string,
    edge: string,
    title: string,
    alpha: number,
): void {
    if (!ctx || radius <= 0.5) return;
    ctx.save();
    ctx.globalAlpha = alpha;
    blobPath(ox, oy, radius, profile);

    const grad = ctx.createRadialGradient(ox, oy, radius * 0.08, ox, oy, radius * 1.05);
    grad.addColorStop(0, fill);
    grad.addColorStop(0.72, fill);
    grad.addColorStop(1, edge);
    ctx.fillStyle = grad;
    ctx.fill();

    // Title stays screen-centered; clipped to the ink so it only shows where covered.
    ctx.clip();
    drawTitle(title);
    ctx.restore();
}

/**
 * Reverse ink: full-frame fill with a clear organic hole that shrinks toward
 * screen center (edges first → closes on CURSORGAME).
 */
function drawInkClose(
    holeRadius: number,
    profile: BlobProfile,
    fill: string,
    title: string,
    alpha: number,
): void {
    if (!ctx) return;
    const w = window.innerWidth;
    const h = window.innerHeight;
    const cx = w * 0.5;
    const cy = h * 0.5;

    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.fillStyle = fill;
    ctx.fillRect(0, 0, w, h);
    drawTitle(title);

    if (holeRadius > 0.5) {
        ctx.globalCompositeOperation = "destination-out";
        blobPath(cx, cy, holeRadius, profile);
        ctx.fill();
    }
    ctx.restore();
}

function clearInk(): void {
    if (!ctx || !canvas) return;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
}

function waitMs(ms: number): Promise<void> {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function animateExpand(
    ox: number,
    oy: number,
    targetR: number,
    profile: BlobProfile,
    fill: string,
    edge: string,
    title: string,
): Promise<void> {
    return new Promise((resolve) => {
        const start = performance.now();
        const tick = (now: number) => {
            if (!running) {
                resolve();
                return;
            }
            const t = Math.min(1, (now - start) / EXPAND_MS);
            const eased = easeOutCubic(t);
            const depth = eased;
            clearInk();
            drawInkBlob(ox, oy, targetR * eased, profile, fill, edge, title, 1);
            hooks?.setTransitionAudioDepth(depth);
            if (t < 1) requestAnimationFrame(tick);
            else resolve();
        };
        requestAnimationFrame(tick);
    });
}

function animateClose(
    targetR: number,
    profile: BlobProfile,
    fill: string,
    title: string,
): Promise<void> {
    return new Promise((resolve) => {
        const start = performance.now();
        const tick = (now: number) => {
            if (!running) {
                resolve();
                return;
            }
            const t = Math.min(1, (now - start) / EXPAND_MS);
            const eased = easeOutCubic(t);
            // Hole shrinks from full cover-radius → 0 (ink closes from edges to center).
            const holeR = targetR * (1 - eased);
            clearInk();
            drawInkClose(holeR, profile, fill, title, 1);
            hooks?.setTransitionAudioDepth(eased);
            if (t < 1) requestAnimationFrame(tick);
            else resolve();
        };
        requestAnimationFrame(tick);
    });
}

function animateFadeOut(
    ox: number,
    oy: number,
    radius: number,
    profile: BlobProfile,
    fill: string,
    edge: string,
    title: string,
): Promise<void> {
    return new Promise((resolve) => {
        const start = performance.now();
        const tick = (now: number) => {
            if (!running) {
                resolve();
                return;
            }
            const t = Math.min(1, (now - start) / FADE_OUT_MS);
            const eased = easeInOutCubic(t);
            clearInk();
            drawInkBlob(ox, oy, radius, profile, fill, edge, title, 1 - eased);
            hooks?.setTransitionAudioDepth(1 - eased);
            if (t < 1) requestAnimationFrame(tick);
            else resolve();
        };
        requestAnimationFrame(tick);
    });
}

function animateFadeOutClose(
    profile: BlobProfile,
    fill: string,
    title: string,
): Promise<void> {
    return new Promise((resolve) => {
        const start = performance.now();
        const tick = (now: number) => {
            if (!running) {
                resolve();
                return;
            }
            const t = Math.min(1, (now - start) / FADE_OUT_MS);
            const eased = easeInOutCubic(t);
            clearInk();
            drawInkClose(0, profile, fill, title, 1 - eased);
            hooks?.setTransitionAudioDepth(1 - eased);
            if (t < 1) requestAnimationFrame(tick);
            else resolve();
        };
        requestAnimationFrame(tick);
    });
}

function beginOverlay(): HTMLElement {
    const el = ensureOverlay();
    resizeCanvas();
    el.classList.remove("hidden");
    el.classList.add("playTransitionOverlay--active");
    el.setAttribute("aria-hidden", "false");
    document.body.classList.add("play-transition-active");
    return el;
}

function endOverlay(el: HTMLElement): void {
    el.classList.remove("playTransitionOverlay--active");
    el.classList.add("hidden");
    el.setAttribute("aria-hidden", "true");
    document.body.classList.remove("play-transition-active");
    clearInk();
    hooks?.setTransitionAudioDepth(0);
    running = false;
}

export function mountPlayTransition(h: PlayTransitionHooks): void {
    hooks = h;
    ensurePointerTracking();
    ensureOverlay();
    clearInk();
}

export function runPlayTransition(startAction: () => void, options: PlayTransitionOptions = {}): void {
    if (!isPlayTransitionEnabled()) {
        startAction();
        return;
    }
    if (running) return;
    running = true;

    ensurePointerTracking();
    const el = beginOverlay();

    const origin = resolveOrigin(options.origin);
    const profile = makeBlobProfile(performance.now() * 0.001 + origin.x * 0.01);
    const targetR = coverRadius(origin.x, origin.y, profile);
    const fill = inkColor();
    const edge = inkEdgeColor();
    const title = inkTitleColor(fill, edge);
    const armLock = options.armPointerLock !== false;

    // Same-frame kick so ink is visible on the PLAY/START gesture frame.
    clearInk();
    drawInkBlob(origin.x, origin.y, Math.max(18, targetR * 0.06), profile, fill, edge, title, 1);
    hooks?.setTransitionAudioDepth(0.12);
    hooks?.playTransitionSfx();
    if (armLock) hooks?.armPointerLockFromGesture();

    void (async () => {
        try {
            await animateExpand(origin.x, origin.y, targetR, profile, fill, edge, title);
            if (HOLD_COVERED_MS > 0) await waitMs(HOLD_COVERED_MS);
            // Load next screen / run under full ink coverage.
            if (running) startAction();
            await animateFadeOut(origin.x, origin.y, targetR, profile, fill, edge, title);
        } finally {
            endOverlay(el);
            // Only for run starts (pointer-lock path), not Home START → hub.
            if (armLock) hooks?.onTransitionComplete?.();
        }
    })();
}

/**
 * Reverse play transition: ink closes from the edges toward screen center
 * (CURSORGAME), then reveals the destination. Same duration/style as expand.
 */
export function runPlayTransitionClose(startAction: () => void): void {
    if (!isPlayTransitionEnabled()) {
        startAction();
        return;
    }
    if (running) {
        // Nested/queued exit while ink is already up — still navigate.
        startAction();
        return;
    }
    running = true;

    ensurePointerTracking();
    const el = beginOverlay();

    const cx = window.innerWidth * 0.5;
    const cy = window.innerHeight * 0.5;
    const profile = makeBlobProfile(performance.now() * 0.001 + 11);
    const targetR = coverRadius(cx, cy, profile);
    const fill = inkColor();
    const edge = inkEdgeColor();
    const title = inkTitleColor(fill, edge);

    // Same-frame kick: ink already hugging the edges.
    clearInk();
    drawInkClose(targetR * 0.94, profile, fill, title, 1);
    hooks?.setTransitionAudioDepth(0.12);
    hooks?.playTransitionSfx();

    void (async () => {
        try {
            await animateClose(targetR, profile, fill, title);
            if (HOLD_COVERED_MS > 0) await waitMs(HOLD_COVERED_MS);
            if (running) startAction();
            await animateFadeOutClose(profile, fill, title);
        } finally {
            endOverlay(el);
        }
    })();
}

/** Run `action` with reverse ink when the setting is on; otherwise immediately. */
export function withExitPlayTransition(action: () => void): void {
    if (!isPlayTransitionEnabled()) {
        action();
        return;
    }
    runPlayTransitionClose(action);
}

/** Modes that begin a run immediately (not sub-screens). */
export const MODES_WITH_PLAY_TRANSITION = new Set([
    "normal",
    "progression",
    "wasd",
    "superdot",
    "roulette",
    "highway",
    "darkness",
    "mirror",
    "snake",
    "combo",
    "colorrush",
    "heat",
    "hunter",
    "pop",
    "counter",
    "aim",
    "hook",
    "orbit",
]);

export function shouldUsePlayTransition(modeId: string): boolean {
    return MODES_WITH_PLAY_TRANSITION.has(modeId) && isPlayTransitionEnabled();
}
